// Orchestration for "Paid to us by the sales rep" (admin). ORDER OF OPERATIONS
// (Rule Zero): Supabase first and AWAITED (createRemittance / reverseRemittance);
// ONLY when that succeeded is this device's SHARED_INVOICES cache touched, through
// the pure cachePatchFromRemittance / cachePatchFromReversal (via
// SHARED_INVOICES.applyRemittance). A failed database write changes nothing local.
// A crash between the two steps leaves the truth in Supabase; the next sync
// (admin mount + realtime) applies the mirror.
import { SHARED_INVOICES } from "./RepPortal";
import { createRemittance, reverseRemittance, getCommissionPaidStateForInvoice } from "./supabase";
import { noteLocalRemittanceMutation, notifyLocalRemittanceChange } from "./remittanceSync";
import { reverseCommissionGate } from "./repRemittances";

const defaultDeps = {
  SHARED_INVOICES, createRemittance, reverseRemittance, getCommissionPaidStateForInvoice,
  noteMutation: noteLocalRemittanceMutation, notifyChange: notifyLocalRemittanceChange,
};

// Bump the stale-fetch counter before the write and after it (a list fetch that started before
// either bump is discarded by its consumer), then tell the open consumers to reload.
const noteBefore = (d) => { try { d.noteMutation && d.noteMutation(); } catch (e) { console.warn("[remittanceActions] noteMutation threw:", e?.message || e); } };
const noteAfter = (d) => {
  try { d.noteMutation && d.noteMutation(); } catch (e) { console.warn("[remittanceActions] noteMutation threw:", e?.message || e); }
  try { d.notifyChange && d.notifyChange(); } catch (e) { console.warn("[remittanceActions] notifyChange threw:", e?.message || e); }
};

/**
 * @param {object} p  inv (cached invoice, read BY VALUE for the rep-id hint only),
 *   form {method, reference, receivedOn, note}, admin {id, email}
 * @returns {Promise<{ok:true,row,warnings:string[],mirrored:boolean,mirrorError?:string}
 *   | {ok:false,error:string,alreadyReceived?:boolean,remittance?:object,missingTable?:boolean,stage?:string,errors?:object}>}
 */
export async function performMarkRemitted({ inv, form, admin } = {}, deps = defaultDeps) {
  const d = deps;
  if (!inv || !inv.id) return { ok: false, error: "No invoice.", stage: "input" };
  const f = form || {};
  noteBefore(d);
  try {
    let res;
    try {
      res = await d.createRemittance({
        invoiceId: inv.id, method: f.method, reference: f.reference, receivedOn: f.receivedOn, note: f.note,
        admin, repIdHint: inv.repId || undefined,
      });
    } catch (e) {
      console.warn("[performMarkRemitted] createRemittance threw for", inv.id, ":", e?.message || e);
      return { ok: false, error: "Recording the payment failed (" + (e?.message || e) + "). Nothing was recorded.", stage: "insert" };
    }
    if (!res || !res.ok) return res || { ok: false, error: "No response.", stage: "insert" };
    // Only now: the local mirror.
    let mirrored = true;
    let mirrorError;
    try { d.SHARED_INVOICES.applyRemittance([res.row]); }
    catch (e) {
      mirrored = false; mirrorError = e?.message || String(e);
      console.warn("[performMarkRemitted] SHARED_INVOICES.applyRemittance mirror failed for", inv.id, ":", mirrorError);
    }
    return { ok: true, row: res.row, warnings: res.warnings || [], mirrored, ...(mirrorError ? { mirrorError } : {}) };
  } finally { noteAfter(d); }
}

/**
 * Reversing after the rep's commission was already marked Paid is never silent: a FRESH read
 * (paged payments reader + corrections) runs first. A failed read blocks (stage "commission_check",
 * never reverses blind). Already-paid commission needs commissionAck === true
 * (needsCommissionAck in the result otherwise). Nothing is written by the check.
 * @param {object} p  remittance (the active row), reason (required), admin {id, email}, commissionAck
 */
export async function performReverseRemittance({ remittance, reason, admin, commissionAck } = {}, deps = defaultDeps) {
  const d = deps;
  if (!remittance || remittance.id === undefined || remittance.id === null) return { ok: false, error: "No remittance.", stage: "input" };
  let paidState;
  try { paidState = await d.getCommissionPaidStateForInvoice(remittance.invoice_id); }
  catch (e) {
    console.warn("[performReverseRemittance] commission check threw for", remittance.invoice_id, ":", e?.message || e);
    paidState = { ok: false, error: e?.message || String(e) };
  }
  const gate = reverseCommissionGate(paidState);
  if (gate.blocked) return { ok: false, error: gate.text, stage: "commission_check", commissionCheckFailed: true };
  if (gate.needsAck && commissionAck !== true) {
    return { ok: false, error: gate.text + " Tick the box to reverse anyway.", stage: "commission_check", needsCommissionAck: true, commissionPaid: true };
  }
  noteBefore(d);
  try {
    let res;
    try { res = await d.reverseRemittance(remittance.id, { reason, admin }); }
    catch (e) {
      console.warn("[performReverseRemittance] reverseRemittance threw for id", remittance.id, ":", e?.message || e);
      return { ok: false, error: "Reversing the payment failed (" + (e?.message || e) + "). Nothing was changed.", stage: "update" };
    }
    if (!res || !res.ok) return res || { ok: false, error: "No response.", stage: "update" };
    let mirrored = true;
    let mirrorError;
    try { d.SHARED_INVOICES.applyRemittance([res.row]); }
    catch (e) {
      mirrored = false; mirrorError = e?.message || String(e);
      console.warn("[performReverseRemittance] SHARED_INVOICES.applyRemittance revert failed for", remittance.invoice_id, ":", mirrorError);
    }
    return { ok: true, row: res.row, mirrored, commissionWasPaid: !!gate.needsAck, ...(mirrorError ? { mirrorError } : {}) };
  } finally { noteAfter(d); }
}
